import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./modal.html";
import { getFocusTrap } from '~/modules/helpers/focusTrap';
import { selectSharedLabels, WireI18n } from '~/modules/stores/i18nStore';

/**
 * Modal to display varying content.
 * It exposes two slots called 'main-content' and 'footer' to display given html
 * @param {string[]} focusableElements All the focusable elements query selectors inside the given slots.
 * @param {string} title The title to display on the modal alongside the close button.
 */
class Modal extends LightningElement {
  constructor(...args) {
    super(...args);
    this.title = '';
    this.closedFocusTarget = void 0;
    this.autoFocusOff = false;
    /**
     * Opt-in "contained scroll" layout: the card is capped to the viewport
     * (below the site header) and bounds the body while the header and footer
     * stay pinned. The body scrolls by default; specialized consumers can
     * delegate that scrolling to a descendant with the exposed CSS custom
     * properties. Default keeps the legacy whole-overlay scroll so existing
     * consumers are unaffected (W-23533535).
     */
    this.bodyScroll = false;
    /**
     * When true, omit the footer slot on phone (`max-width: 767px`) so unused
     * empty footers do not take layout space. Desktop is unchanged.
     */
    this.hideFooterOnPhone = false;
    this.isPhoneViewport = false;
    this.onPhoneMediaChange = event => {
      this.isPhoneViewport = event.matches;
    };
    this.sharedLabels = void 0;
  }
  connectedCallback() {
    // This will keep the body from scrolling while the modal is open.
    const styles = document.body.getAttribute('style') || '';
    document.body.setAttribute('style', styles.concat(' overflow-y: hidden;'));
    if (typeof window.matchMedia === 'function') {
      this.phoneMedia = window.matchMedia('(max-width: 767px)');
      this.isPhoneViewport = this.phoneMedia.matches;
      if (typeof this.phoneMedia.addEventListener === 'function') {
        this.phoneMedia.addEventListener('change', this.onPhoneMediaChange);
      }
    }
  }
  renderedCallback() {
    const container = this.modalContainerRef;
    if (!container) {
      return;
    }
    this.focusTrap = getFocusTrap(container);
    this.focusFirstInput();
  }
  disconnectedCallback() {
    if (this.phoneMedia && typeof this.phoneMedia.removeEventListener === 'function') {
      this.phoneMedia.removeEventListener('change', this.onPhoneMediaChange);
    }
    this.phoneMedia = undefined;
    const styles = document.body.getAttribute('style');
    // @ts-expect-error * Automated Type Error Fix
    document.body.setAttribute('style', styles.replace(' overflow-y: hidden;', ''));
  }
  get showFooter() {
    return !(this.hideFooterOnPhone && this.isPhoneViewport);
  }
  get modalContainerRef() {
    // @ts-expect-error * Automated Type Error Fix
    return this.template.querySelector('.modal');
  }
  get closeIconDescription() {
    return this.sharedLabels.close;
  }
  get wrapperClass() {
    return this.bodyScroll ? 'modal-wrapper modal-wrapper--body-scroll' : 'modal-wrapper';
  }
  get contentClass() {
    return this.bodyScroll ? 'content content--body-scroll' : 'content';
  }
  closeModal() {
    this.dispatchEvent(new CustomEvent('close', {
      detail: {
        closedFocusTarget: this.closedFocusTarget
      }
    }));
  }

  // @ts-expect-error * Automated Type Error Fix
  handleNextFocus(event) {
    const {
      focusNext
    } = this.focusTrap;
    const {
      shouldPreventDefault
    } = focusNext();
    if (shouldPreventDefault) {
      event.preventDefault();
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handlePrevFocus(event) {
    const {
      focusPrevious
    } = this.focusTrap;
    const {
      shouldPreventDefault
    } = focusPrevious();
    if (shouldPreventDefault) {
      event.preventDefault();
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handleInputKey(event) {
    if (event.key === 'Escape') {
      this.closeModal();
    }
    if (event.key === 'Tab') {
      if (event.shiftKey) {
        this.handlePrevFocus(event);
      } else {
        this.handleNextFocus(event);
      }
    }
  }
  focusFirstInput() {
    if (this.autoFocusOff || !this.focusTrap) {
      return;
    }
    // If more than one focusable element exists, focus the second one.
    // If there's only one focusable element, it's the modal's close button, and it will be focused.
    if (this.focusTrap.getFocusableElementsQuantity() > 1) {
      this.focusTrap.focusElementByIndex(1);
      return;
    }
    this.focusTrap.focusFirstElement();
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(Modal, {
  publicProps: {
    title: {
      config: 0
    },
    closedFocusTarget: {
      config: 0
    },
    autoFocusOff: {
      config: 0
    },
    bodyScroll: {
      config: 0
    },
    hideFooterOnPhone: {
      config: 0
    }
  },
  track: {
    isPhoneViewport: 1
  },
  wire: {
    sharedLabels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: selectSharedLabels
        };
      }
    }
  },
  fields: ["onPhoneMediaChange"]
});
const __lwc_component_class_internal = _registerComponent(Modal, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;